import {
  InputCheck,
  InputDate,
  InputSelect,
  InputText,
  InputTypeahead,
} from '@farmshare/ui-components';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useField } from 'formik';
import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';

import {
  type CustomFieldDefinitionManyQuery,
  EnumCustomFieldDefinitionCaptureAt,
  EnumCustomFieldDefinitionFieldType,
  EnumCustomFieldDefinitionModelType,
  useCustomFieldDefinitionManyQuery,
} from '../../lib/graphql';

export type CustomFieldDef =
  CustomFieldDefinitionManyQuery['customFieldDefinitionMany'][number];

/**
 * Hook to fetch and filter custom field definitions for a processor.
 * Returns both standard and custom fields, filtered by enabled + species.
 * @param root0
 * @param root0.vendorId
 * @param root0.species
 * @param root0.captureAt
 * @param root0.modelType
 * @param root0.enabled
 */
export function useCustomFieldDefs({
  vendorId,
  species,
  captureAt,
  modelType = EnumCustomFieldDefinitionModelType.Head,
  enabled = true,
}: {
  vendorId: string;
  species: string;
  captureAt: EnumCustomFieldDefinitionCaptureAt;
  modelType?: EnumCustomFieldDefinitionModelType;
  /**
   * Gate the query off when the caller can't read definitions. `customFieldDefinitionMany`
   * is processor-only on the backend (`processorRoleOnly`), so producer-context callers
   * (e.g. the Request-a-Job wizard's review step) must skip it — otherwise it throws
   * "Processor access required". Defaults to enabled for the processor callers.
   */
  enabled?: boolean;
}) {
  const { data, loading } = useCustomFieldDefinitionManyQuery({
    variables: {
      filter: {
        vendor: vendorId,
        captureAt,
        modelType,
      },
    },
    skip: !vendorId || !enabled,
  });

  const allDefs = data?.customFieldDefinitionMany;

  const definitions = useMemo(() => {
    const filtered = (allDefs ?? []).filter((def) => {
      if (def.enabled === false) return false;
      if (!species) return true;
      if (def.speciesFilter === 'all') return true;
      if (Array.isArray(def.speciesFilter)) {
        return def.speciesFilter.includes(species);
      }
      return true;
    });
    // Standard fields first (in their seeded order), then custom fields
    const standard = sortBy(
      filtered.filter((d) => d.fieldSource === 'standard'),
      'sortOrder',
    );
    const custom = sortBy(
      filtered.filter((d) => d.fieldSource !== 'standard'),
      'sortOrder',
    );
    return [...standard, ...custom];
  }, [allDefs, species]);

  return { definitions, loading };
}

function getNameOverride(definition: CustomFieldDef, fieldPrefix: string) {
  const prefix = fieldPrefix ? `${fieldPrefix}.` : '';
  if (definition.fieldSource === 'standard') {
    return `${prefix}${definition.fieldKey}`;
  }
  return `${prefix}customFieldValues.${definition._id}`;
}

/**
 * A required toggle can't be answered "No" with a plain switch — an unchecked
 * switch is indistinguishable from "unanswered", so a required toggle would
 * force itself on (SUP-282: required +30 Months could only be submitted as Yes).
 * Render required toggles as an explicit Yes/No selector that still stores a
 * boolean, so submit/serialize paths are unchanged. `undefined` means
 * unanswered (fails required); `false` is a valid "No".
 * @param root0
 * @param root0.definition
 * @param root0.fieldPrefix
 * @param root0.size
 */
function ToggleYesNoInput({
  definition,
  fieldPrefix,
  size = undefined,
}: {
  definition: CustomFieldDef;
  fieldPrefix: string;
  size?: 'small' | 'medium';
}) {
  const name = getNameOverride(definition, fieldPrefix);
  const { label, required, hint } = definition;
  const validate = useCallback(
    (value: unknown) =>
      required && (value === null || value === undefined)
        ? `${label} is required.`
        : undefined,
    [required, label],
  );
  const [field, meta, helpers] = useField<boolean | null | undefined>({
    name,
    validate,
  });
  const showError = Boolean(meta.touched && meta.error);
  let selected: 'yes' | 'no' | '';
  if (field.value === true) selected = 'yes';
  else if (field.value === false) selected = 'no';
  else selected = '';

  return (
    <FormControl
      fullWidth
      size={size ?? 'small'}
      required={!!required}
      error={showError}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={selected}
        onChange={(e) => {
          const v = e.target.value;
          let next: boolean | undefined;
          if (v === 'yes') next = true;
          else if (v === 'no') next = false;
          else next = undefined;
          helpers.setValue(next);
        }}
        onBlur={() => helpers.setTouched(true)}
      >
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </Select>
      {(showError || hint) && (
        <FormHelperText>{showError ? meta.error : hint}</FormHelperText>
      )}
    </FormControl>
  );
}

const TEXT_INPUT_TYPE_BY_FIELD_TYPE: Partial<
  Record<EnumCustomFieldDefinitionFieldType, 'number' | 'text'>
> = {
  [EnumCustomFieldDefinitionFieldType.Number]: 'number',
  [EnumCustomFieldDefinitionFieldType.Text]: 'text',
};

export function RegistryFieldInput({
  definition,
  fieldPrefix,
  size = undefined,
}: {
  definition: CustomFieldDef;
  fieldPrefix: string;
  /** Forwarded to underlying MUI inputs so callers can match surrounding form density. */
  size?: 'small' | 'medium';
}) {
  const { fieldType, label, options, required, hint } = definition;
  const nameOverride = getNameOverride(definition, fieldPrefix);

  // Stable references so InputSelect's autoSelectFirstOption / InputTypeahead
  // option-watching effects don't re-fire setFieldValue on every render.
  const selectOptions = useMemo(
    () =>
      options?.filter(Boolean).map((opt) => ({
        value: opt!,
        label: opt!,
      })) ?? [],
    [options],
  );
  const multiSelectOptions = useMemo(
    () =>
      options?.filter(Boolean).map((opt) => ({
        _id: opt!,
        label: opt!,
      })) ?? [],
    [options],
  );

  switch (fieldType) {
    case EnumCustomFieldDefinitionFieldType.HangingWeight:
      // Compound field — must be rendered by the parent via the
      // HangingWeightInput component, passed in as `renderHangingWeight`.
      // Hitting this arm directly means a caller bypassed FieldRegistryFormSection.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `RegistryFieldInput: HangingWeight field "${label}" rendered without a renderHangingWeight slot — caller should use FieldRegistryFormSection.`,
        );
      }
      return null;

    case EnumCustomFieldDefinitionFieldType.Toggle:
      // Required toggles render as an explicit Yes/No selector so they can be
      // answered "No" (a required switch would otherwise force "Yes" — SUP-282).
      if (required) {
        return (
          <ToggleYesNoInput
            definition={definition}
            fieldPrefix={fieldPrefix}
            size={size}
          />
        );
      }
      return (
        <InputCheck
          label={label}
          type="switch"
          nameOverride={nameOverride}
          hint={hint ?? undefined}
          required={!!required}
        />
      );

    case EnumCustomFieldDefinitionFieldType.SingleSelect:
      return (
        <InputSelect
          label={label}
          nameOverride={nameOverride}
          options={selectOptions}
          required={!!required}
          size={size}
        />
      );

    case EnumCustomFieldDefinitionFieldType.MultiSelect:
      return (
        <InputTypeahead<string>
          label={label}
          nameOverride={nameOverride}
          multiple
          options={multiSelectOptions}
          required={!!required}
          hint={hint ?? undefined}
          size={size}
        />
      );

    case EnumCustomFieldDefinitionFieldType.Date:
      // A real picker, not `InputText type="date"` — the bare browser control
      // has no calendar button in every browser, and its year segment commits
      // after two typed digits, so a hand-typed "2026" was stored as year 0020
      // (SUP-426). `InputDate` emits the same `YYYY-MM-DD` string, so the
      // serialize/read paths are unchanged.
      return (
        <InputDate
          label={label}
          nameOverride={nameOverride}
          hint={hint ?? undefined}
          required={!!required}
          size={size}
        />
      );

    case EnumCustomFieldDefinitionFieldType.Number:
    case EnumCustomFieldDefinitionFieldType.Text:
    default:
      return (
        <InputText
          label={label}
          type={TEXT_INPUT_TYPE_BY_FIELD_TYPE[fieldType] ?? 'text'}
          nameOverride={nameOverride}
          hint={hint ?? undefined}
          required={!!required}
          floatingLabel
          size={size}
        />
      );
  }
}

function FieldRows({
  definitions,
  fieldPrefix,
  renderHangingWeight = undefined,
}: {
  definitions: CustomFieldDef[];
  fieldPrefix: string;
  renderHangingWeight?: React.ReactNode;
}) {
  const rows: JSX.Element[] = [];
  let i = 0;

  while (i < definitions.length) {
    const def = definitions[i];
    const isHangingWeight =
      def.fieldType === EnumCustomFieldDefinitionFieldType.HangingWeight;

    if (isHangingWeight) {
      // Hanging weight gets its own row — use custom component if provided
      rows.push(
        <Row className="g-2" key={def._id}>
          <Col xs={12}>
            {renderHangingWeight ?? (
              <RegistryFieldInput definition={def} fieldPrefix={fieldPrefix} />
            )}
          </Col>
        </Row>,
      );
      i++;
    } else {
      // Pair fields in two-column rows
      const next = definitions[i + 1];
      const nextIsHangingWeight =
        next?.fieldType === EnumCustomFieldDefinitionFieldType.HangingWeight;

      rows.push(
        <Row className="g-2" key={def._id}>
          <Col xs={12} md={6}>
            <RegistryFieldInput definition={def} fieldPrefix={fieldPrefix} />
          </Col>
          {next && !nextIsHangingWeight && (
            <Col xs={12} md={6}>
              <RegistryFieldInput definition={next} fieldPrefix={fieldPrefix} />
            </Col>
          )}
        </Row>,
      );
      i += next && !nextIsHangingWeight ? 2 : 1;
    }
  }

  return <div className="d-flex flex-column gap-3">{rows}</div>;
}

/**
 * Renders field inputs for a single record (head/job/split).
 * Handles both standard and custom field types.
 *
 * For standard fields, `nameOverride` uses `{fieldPrefix}.{fieldKey}`.
 * For custom fields, it uses `{fieldPrefix}.customFieldValues.{definitionId}`.
 * @param root0
 * @param root0.definitions
 * @param root0.fieldPrefix
 * @param root0.renderHangingWeight
 */
export function FieldRegistryFormSection({
  definitions,
  fieldPrefix,
  renderHangingWeight = undefined,
}: {
  definitions: CustomFieldDef[];
  fieldPrefix: string;
  /** Optional custom component to render for hanging_weight fields */
  renderHangingWeight?: React.ReactNode;
}) {
  if (definitions.length === 0) return null;

  return (
    <FieldRows
      definitions={definitions}
      fieldPrefix={fieldPrefix}
      renderHangingWeight={renderHangingWeight}
    />
  );
}
